export interface IInternalControlsState {
  error: boolean | null;
  loading: boolean;
  drawTable: boolean;
  selectedEntityId: number | undefined;
  saveDisabled: boolean;
  hasChanges: boolean;
}

export interface LookupValue {
  id: number | null;
  name: string | null;
}

export interface DropdownItem {
  id: number;
  name: string;
  title?: string; // Optional for business process items that use title instead of name
}

// API Response interfaces
export interface ApiProcessItem {
  id: number;
  title: string;
  description: string;
  description1: string;
  description2: string;
  process_image: string;
  process_risk: string;
  responsible_person: string;
  start_date: string;
  end_date: string;
  activation_date: string;
  active: string;
  deleted: string;
  version: string;
  comments: string;
  owner_maintainer: string;
  created_at: string;
  updated_at: string;
  process_type_id: number | null;
  process_type_name: string | null;
  business_process_l1_id: number | null;
  business_process_l1_name: string | null;
  business_process_l2_id: number | null;
  business_process_l2_name: string | null;
  business_process_l3_id: number | null;
  business_process_l3_name: string | null;
  process_criticality_id: number | null;
  process_criticality_name: string | null;
  group_id?: number | null;
  group_name?: string | null;
  division_id?: number | null;
  division_name?: string | null;
  region_id?: number | null;
  region_name?: string | null;
  legal_entity_id?: number | null;
  legal_entity_name?: string | null;
  business_unit_id?: number | null;
  business_unit_name?: string | null;
}

// Error handling interfaces
export interface ErrorItem {
  index: number;
  error: string;
}

export interface ApiError {
  errors?: ErrorItem[];
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

export interface LinkedRisk {
  id: number;
  title: string;
}

export interface ControlRow {
  controlImage: null;
  active: boolean;
  comments: null;
  instanceActive: boolean;
  instanceComments: null;
  evidenceFile1: string | null;
  evidenceFile2?: string | null;
  instanceStartDate: null;
  id: any;
  key: string;
  controlName: string;
  controlDescription: string;
  controlDescription1: string;
  linkedRisks?: LinkedRisk[];
  controlObjective: string;
  keyControl: boolean;
  owner: LookupValue;
  controlType: LookupValue;
  controlNature: LookupValue;
  controlFrequency: LookupValue;
  controlLevels: LookupValue;
  performerToD: LookupValue | string;
  performerToE: LookupValue | string;
  approvers: LookupValue | string;
  controlPerformer: LookupValue;
  designEffectiveness: LookupValue;
  operatingEffectiveness: LookupValue;
  controlStatus: LookupValue;
  controlOrigin: LookupValue;
  controlCustodian: LookupValue;
  reasonForControlStatus: LookupValue;
  controlPrerequisites?: LookupValue;
  controlOverallEffectiveness?: LookupValue;
  testingMethod?: LookupValue;
  testingMethod2?: LookupValue;
  evidenceType?: LookupValue;
  testType?: LookupValue;
  testObjectiveness?: LookupValue | string;
  testObjectiveness2?: LookupValue;
  testDescription?: string;
  testDescription1?: string;
  testDescription2?: string;
  evidenceDescription: string;
  evidence_description_2: string;
  testResult?: LookupValue;
  lastTestDateDesign?: any;
  lastTestDateOperational?: any;
  nextTestDate?: any;
  endDate?: any;
  startDate?: any;
  notUsed?: boolean;
  instance_id?: number;
  isNew?: boolean;
  monitoringlevel?: string;
  _pinned?: boolean;
}

export interface RiskRow {
  id: any;
  key: string;
  title: string;
  description: string;
  description1: string;
  description2: string;
  category: string;
  root_cause: string;
  event: string;
  impact_consequence: string;
  probability_of_occurrence: LookupValue;
  severity: LookupValue;
  risk_assessment_score: string | null;
  speed_of_occurrence: LookupValue;
  gross_risk: number | null;
  residual_risk: number | null;
  risk_owner: string;
  risk_status: LookupValue;
  assessment_date: any;
  update_cycle: LookupValue;
  key_risk_indicators: string;
  risk_trend: LookupValue;
  owner_maintainer: string;
  start_date: any;
  end_date: any;
  created_at: any;
  updated_at: any;
  notUsed?: boolean;
  instance_id: number;
  isNew?: boolean;
  _pinned?: boolean;
}

export interface ProcessRow {
  id: any;
  key: string;
  title: string;
  description: string;
  description1: string;
  description2: string;
  process_image: string;
  process_risk: string;
  responsible_person: string;
  start_date: string;
  end_date: string;
  activation_date: string;
  active: string;
  deleted: string;
  version: string;
  comments: string;
  owner_maintainer: string;
  created_at: string;
  updated_at: string;
  process_type: LookupValue;
  business_process_l1?: LookupValue;
  business_process_l2?: LookupValue;
  business_process_l3?: LookupValue;
  group?: LookupValue;
  division?: LookupValue;
  region?: LookupValue;
  legal_entity?: LookupValue;
  business_unit?: LookupValue;
  process_criticality?: LookupValue;
  isNew?: boolean;
  _pinned?: boolean;
}

export type ProcessStep = {
  id: string;
  label: string;
  description?: string;
  next?: string[];
};

export type BusinessProcess = {
  id: string;
  title: string;
  steps: Record<string, ProcessStep>;
};

export type DragItem = {
  id: number;
  text: string;
  itemType: 'RISK' | 'CONTROL' | 'BPR';
  columnId: string;
  disabled?: boolean;
  severity?: Severity;
  criticality?: { id: number; name: string };
  description?: string;
};

export type Severity = {
  id: number;
  name: string;
};

export type Instance = {
  instance_id: number;
  severity?: Severity;
};

export type ColumnItem = {
  description?: string | undefined;
  id: number;
  text: string;
  type: 'BPR' | 'OTHER';
  notUsed?: boolean;
  category?: string;
  severity?: Severity;
  process_criticality?: { id: number; name: string };
  control_title?: string;
  instances?: Instance[];
};

export type Column = {
  id: string;
  title: string;
  canDrop: boolean;
  items: ColumnItem[];
  description?: string;
};

export type Assignment = {
  assignmentId: string;
  bprId: number;
  riskInstanceId: string | null;
  sourceType: 'RISK' | 'CONTROL';
  sourceId: number;
  text: string;
  severity?: { id: number; name: SeverityName } | null;
  description?: string;
};

export interface IInternalControlLayoutProps {
  activeTab?: string;
  activeKey?: string;
  children: JSX.Element;
  onSave?: (() => void) | ((query?: Record<string, any>) => Promise<void>);
  additionalFilters?: React.ReactNode;
}

export interface Process {
  key: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  processType: string;
  responsiblePerson: string;
}

export type Entity = {
  id: number;
  name: string;
  [key: string]: any;
};

export type EntityMap<T extends Entity> = Map<number, T>;

export type Scope = {
  group_id?: number | null;
  division_id?: number | null;
  region_id?: number | null;
  legal_entity_id?: number | null;
  business_unit_id?: number | null;
  main_organization_id?: number | null | number[];
};

export type AllocateProcessRiskRequest = {
  business_process_l4_id: number;
  scope: Scope;
  start_date: string;
  version: number;
  comments: string;
  owner_maintainer: string;
  risk_ids: number[];
};

export type AllocateProcessRiskControlRequest = {
  business_process_l4_id: number;
  risk_maintenance_id: number;
  scope: Scope;
  start_date: string;
  version: number;
  comments: string;
  owner_maintainer: string;
  control_ids?: number[];
};

export type SelectOption = {
  label: string;
  value: number;
};

export const emptyFilledFilters = [
  { text: 'Filled', value: 'filled' },
  { text: 'Empty', value: 'empty' }
];

export type SeverityName =
  | 'Negligible'
  | 'Low'
  | 'Medium'
  | 'High'
  | 'Very High'
  | 'Extreme';

export const SEVERITY_COLOR_MAP: Record<string, string> = {
  Extreme: '#B2010D',
  'Very High': '#E81710',
  High: '#FA7A02',
  Medium: '#F9B602',
  Low: '#317B33',
  Negligible: '#8DBA2E'
};

export const PROCESS_CRITICALITY_COLOR_MAP: Record<string, string> = {
  Critical: '#F07A67',
  High: '#FA7A02',
  Medium: '#F9B602',
  Low: '#6FCF97'
};

export interface RiskSeverityLevelProps {
  severity?: string | null;
}

export interface DraggableElementProps {
  isDragging?: boolean;
  criticality?: string;
}

export type ProcessParentType =
  | 'group'
  | 'division'
  | 'region'
  | 'legal_entity'
  | 'business_unit';

export type SearchKey =
  | 'title'
  | 'description'
  | 'activation'
  | 'type'
  | 'responsible'
  | 'version'
  | 'comments'
  | 'owner';

export type SearchStates = Record<
  SearchKey,
  {
    search: string;
    setSearch: (text: string) => void;
    showSearch: boolean;
    setShowSearch: (show: boolean) => void;
  }
>;

export type FilterKey =
  | 'title'
  | 'description'
  | 'activation'
  | 'type'
  | 'responsible'
  | 'version'
  | 'comments'
  | 'owner';

export interface QueryScope {
  group_id: number | null;
  division_id: number | null;
  region_id: number | null;
  legal_entity_id: number | null;
  business_unit_id: number | null;
  main_organization_id?: number | null | undefined;
}

export type MaybeLookup = LookupValue | number | string | null | undefined;

export interface ControlAttestationRow {
  id: number | string;

  // ───────── CONTROL LEVEL ─────────
  controlName: string;
  controlDescription: string;

  start_date?: string | null;
  end_date?: string | null;
  created_at?: string | null;

  keyControl?: boolean;

  testingMethod?: MaybeLookup;

  testType?: MaybeLookup;

  // ───────── INSTANCE SCOPE / ORG STRUCTURE ─────────
  instance_id?: number | string | null;

  group?: MaybeLookup;
  division?: MaybeLookup;
  region?: MaybeLookup;
  legal_entity?: MaybeLookup;
  business_unit?: MaybeLookup;

  // ───────── PEOPLE ─────────
  performer_toe?: MaybeLookup;
  approver?: MaybeLookup;

  // ───────── TESTING DATES ─────────
  last_test_date_operational?: string | null;
  next_test_date?: string | null;

  // ───────── TESTING FIELDS ─────────
  test_objectiveness_3?: string | null;
  test_description_3?: string | null;
  evidence_description_3?: string | null;

  evidence_file_3?: string | null;
  description_3?: string | null;

  testResult?: LookupValue;
  evidenceType?: MaybeLookup;

  controlAttestationEffectiveness?: MaybeLookup;

  testStatus?: MaybeLookup;
  control_effectiveness?: MaybeLookup;
  assertion_area?: MaybeLookup;

  // ───────── UI STATE ─────────
  notUsed?: boolean;

  // optional UI-only fields if you use them elsewhere
  active?: boolean;
  comments?: string | null;
}
